// ProjectFacet — პროექტი, the building typology ladder. Owner ruling 2026-08-28.
//
// 🔴 41,872 PROPERTY-CODES AND NO WAY TO ASK FOR THEM. The codes have always been filterable —
// `features=proj_khrushchovka` answers 62 on the live server today — but the derived panel
// correctly EXCLUDES them, because axisOf classifies proj_* as an exclusive axis and three
// checkboxes for one exclusive question let an agent tick two and get nothing. Renovation, frame
// and build-status all got their own control when they were excluded. Typology never did. It was
// correctly removed and then never put back.
//
// ═══ THE GROUPING IS THE OWNER'S, AND IT IS NOT COSMETIC ═══
//
// არასტანდარტული does not mean "other". Owner: it means anything that is NOT one of the old
// standardised series — so it covers modern construction AND old buildings that were individual
// projects. Measured: of its 33,727 properties, 23,003 are new-built and 7,370 are OLD. It is
// therefore NOT a synonym for "new build" and must not be folded into the build-status ladder;
// they overlap heavily and answer different questions.
//
// ⚠️ თბილისური ეზო IS NOT A SOVIET SERIES. It is 19th / early-20th century, pre-Soviet, and
// grouping it under „საბჭოთა სერია" would be historically wrong even though agents use it as a
// typology like any other. It gets its own heading.
//
// ⚠️ ქალაქური IS one, despite the generic name — a specific standardised 8-storey project built
// in Tbilisi in the 1960s–80s, with a loggia and higher ceilings than a khrushchovka. It belongs
// beside მოსკოვი and ჩეხური, not in a second catch-all.
//
// ═══ SINGLE-SELECT, BECAUSE A BUILDING IS ONE PROJECT ═══
//
// Picking a second replaces the first; picking the selected one clears it. That is what makes it
// safe to write into the ordinary featureCodes list: two proj_ codes would AND together and
// return nothing, which is the exact failure the axis exclusion exists to prevent.
//
// ⚠️ THE COUNTS ARE CORPUS-WIDE and never view-scoped — the reason is on LadderRow.
import React from 'react'
import LadderRow from './LadderRow.jsx'

// Code → group. The ONLY constant here: labels and counts come from the live vocabulary, so a
// renamed series or a new one needs no edit unless it needs a different group.
const HISTORICAL = new Set(['proj_tbilisi_yard'])
const NONSTANDARD = 'proj_nonstandard'

export default function ProjectFacet({ features = [], selected = null, onSelect, t }) {
  const tr = t || (k => k)
  const rows = (features || []).filter(f => f && typeof f.code === 'string' && f.code.startsWith('proj_') && f.active !== false)
  if (!rows.length) return null

  const label = (f) => f.name_ka || f.name_en || f.code
  const byUsage = (a, b) => (Number(b.usage) || 0) - (Number(a.usage) || 0)
  const nonstandard = rows.find(f => f.code === NONSTANDARD)
  const historical = rows.filter(f => HISTORICAL.has(f.code)).sort(byUsage)
  const standard = rows.filter(f => f.code !== NONSTANDARD && !HISTORICAL.has(f.code)).sort(byUsage)

  // The rung itself is shared with FurnishingFacet — see LadderRow.jsx. Two ladders that
  // disagreed about what a second click does would be two bugs, not one component.
  const Row = ({ f }) => (
    <LadderRow code={f.code} label={label(f)} count={f.usage}
      selected={selected === f.code} onSelect={onSelect} />
  )

  const Group = ({ titleKey, items }) => items.length ? (
    <div className="space-y-0.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mt-1">{tr(titleKey)}</p>
      {items.map(f => <Row key={f.code} f={f} />)}
    </div>
  ) : null

  return (
    <div className="space-y-1.5">
      {nonstandard && <Row f={nonstandard} />}
      <Group titleKey="desk_project_historical" items={historical} />
      <Group titleKey="desk_project_standard" items={standard} />
    </div>
  )
}
